<template>
  <div :class="['m-table-wrapper', layoutPadding ? '-layout-padding' : '']">
    <table class="m-table">
      <thead>
        <tr>
          <th
            v-for="column in columns"
            :key="column.key"
            :class="[
              '_head',
              column.sticky || stickyHeader ? '-sticky' : '',
              column.sticky ? '-sticky-col' : '',
              stickyHeader ? '-sticky-header' : '',
            ]"
            :style="{
              width: column.width === undefined ? null : `${column.width}px`,
            }"
          >
            <div
              :class="['_header-cell']"
              :style="{ marginRight: headerCellPadding(column) }"
            >
              <div
                v-if="column.scopedSlots?.title === undefined"
                class="_title"
              >
                <div
                  :class="[
                    '_title-left',
                    column.sorter !== undefined ? '-clickable' : '',
                    column.sorter !== undefined && !localOrder[column.key] ? '-sorter-space' : '',
                  ]"
                  @click="handleHeaderClick(column)"
                >
                  <div
                    v-if="column.icon !== '' && column.icon !== undefined"
                    class="_title-icon"
                  >
                    <m-icon :type="column.icon" />
                  </div>
                  <div class="_title-text">
                    {{ column.title }}
                  </div>
                  <m-icon
                    v-if="localOrder[column.key]"
                    :type="localOrder[column.key] === 'up' ? 'order-up' : 'order-down'"
                    :color="$colors.grey.darken4"
                    size="12"
                    class="_title-sorter"
                  />
                </div>
                <div
                  v-if="column.scopedSlots?.titleRight !== undefined"
                  class="_title-right"
                >
                  <slot :name="column.scopedSlots.titleRight" />
                </div>
              </div>
              <template v-else>
                <slot :name="column.scopedSlots.title" />
              </template>
              <div
                v-if="column.onFilter !== undefined"
                class="_actions"
              >
                <m-dropdown
                  :title="$t('mTable.search', { title: column.title })"
                  class="_filter"
                  stop-trigger-event-propagation
                >
                  <m-btn
                    :icon-color="iconColor(column)"
                    :button-style="{ height: '2.4rem', width: '2.4rem' }"
                    :icon="column.filterOptions !== undefined ? 'filter' : 'search'"
                    small
                    fab
                    hide-border
                    light
                  />
                  <template #overlay>
                    <m-card
                      padding-xs
                      class="_filter-overlay"
                    >
                      <div
                        v-if="column.filterOptions !== undefined"
                        class="_filter-options"
                        @mouseleave="focusedFilterOption = null"
                      >
                        <m-select-item
                          :item="{}"
                          :selected="!search[column.key]"
                          :focused="focusedFilterOption === ''"
                          @click="setColumnFilter(column, '')"
                          @focus="focusedFilterOption = ''"
                        >
                          {{ $t('general.any') }}
                        </m-select-item>
                        <m-select-item
                          v-for="option in column.filterOptions"
                          :key="option.value"
                          :item="option"
                          :selected="search[column.key] === option.value"
                          :focused="focusedFilterOption === option.value"
                          @click="setColumnFilter(column, option.value)"
                          @focus="focusedFilterOption = option.value"
                        >
                          {{ option.label }}
                        </m-select-item>
                      </div>
                      <m-text-field
                        v-else
                        v-model:value="search[column.key]"
                        :placeholder="`${$t('mTable.search', { title: column.title })}`"
                        auto-focus
                        allow-clear
                      />
                    </m-card>
                  </template>
                </m-dropdown>
              </div>
            </div>
          </th>
        </tr>
      </thead>
      <tbody :class="['m-table-tbody', loading ? '-loading' : '']">
        <tr v-if="paginatedData.length === 0">
          <td
            :colspan="columns.length"
            class="_empty"
          >
            {{ $t('mTable.noData') }}
          </td>
        </tr>
        <tr
          v-for="row in paginatedData"
          :key="byString(row, rowKey)"
          :class="[
            'm-table-row',
            rowsClickable || row.clickable ? '-clickable' : '',
            rowClassName,
            selectedRows.includes(byString(row, rowKey)) ? '-selected' : '',
            row.disabled ? '-disabled' : '',
          ]"
          :data-id="byString(row, rowKey)"
          @click="handleRowClick(row)"
          @contextmenu="handleContextMenu($event, row)"
        >
          <td
            v-for="(col, index) in columns"
            :key="col.key"
            :style="col.sticky ? { position: 'sticky', left: 0, backgroundColor: 'white' } : null"
          >
            <template v-if="index === 0 && showDragOverTopTargets.includes(byString(row, rowKey))">
              <slot
                name="dragzoneTop"
                :row="row"
              >
                <m-dragzone class="_dragzone-top" />
              </slot>
            </template>
            <div class="_cell">
              <template v-if="col.scopedSlots === undefined">
                {{ byString(row, `${col.dataIndex}`) }}
              </template>
              <slot
                v-else
                :name="col.scopedSlots.customRender"
                :[`${col.scopedSlots.customRender}`]="byString(row, `${col.dataIndex}`)"
                :row="row"
              />
            </div>
            <template v-if="index === 0 && showDragOverBottomTargets.includes(byString(row, rowKey))">
              <slot
                name="dragzoneBottom"
                :row="row"
              >
                <m-dragzone class="_dragzone-bottom" />
              </slot>
            </template>
          </td>
        </tr>
        <tr
          v-if="showLoadMoreButton"
          @click="handleLoadMoreClick"
        >
          <td
            colspan="100%"
            :class="[
              '_row-btn',
              rowsClickable ? '-clickable' : '',
            ]"
          >
            <div class="_content">
              <m-icon
                :color="$colors.grey.lighten1"
                type="down"
                class="_icon"
              />
              {{ $t('general.loadMore') }}
            </div>
          </td>
        </tr>
        <tr
          v-if="showNewButton"
          @click="$emit('new')"
        >
          <td
            colspan="100%"
            :class="[
              '_row-btn',
              newButtonLoading ? '-loading' : '',
              rowsClickable ? '-clickable' : '',
            ]"
          >
            <div class="_content">
              <m-icon
                :color="$colors.grey.lighten1"
                :type="newButtonLoading ? 'loading' : 'plus'"
                :spin="newButtonLoading"
                class="_icon"
              />
              {{ newButtonLabel }}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <div class="_bottom">
      <div
        v-if="showCount"
        class="_count"
      >
        <div class="_label">
          {{ $t('mTable.count') }}
        </div>
        <div class="_value">
          {{ preparedDataSource.length }}
        </div>
      </div>
      <div
        v-if="showPagination"
        class="_pagination"
      >
        <m-pagination
          v-model:value="page"
          :total="preparedDataSource.length"
          :items-per-page="pagination.pageSize"
          hide-border
        />
      </div>
    </div>
  </div>
</template>

<script>
import MBtn from 'shared/components/base/MBtn.vue';
import MCard from 'shared/components/base/MCard.vue';
import MDragzone from 'shared/components/base/MDragzone.vue';
import MDropdown from 'shared/components/base/MDropdown.vue';
import MIcon from 'shared/components/base/MIcon.vue';
import MPagination from 'shared/components/base/MPagination.vue';
import MSelectItem from 'shared/components/base/MSelectItem.vue';
import MTextField from 'shared/components/base/MTextField.vue';
import { copy } from 'shared/lib/copy';
import { paginationType } from 'shared/constants.json';

export default {
  name: 'MTable',
  props: {
    columns: {
      type: Array,
      required: true,
    },
    dataSource: {
      type: Array,
      required: true,
    },
    customRow: {
      type: Function,
      default: () => ({
        on: {
          click() {
          },
        },
      }),
    },
    layoutPadding: {
      type: Boolean,
      default: false,
    },
    rowsClickable: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: [Boolean, Object],
      default: false,
    },
    paginationType: {
      type: String,
      default: paginationType.replace,
    },
    showCount: {
      type: Boolean,
      default: false,
    },
    rowKey: {
      type: String,
      default: 'key',
    },
    selectedRows: {
      type: Array,
      default: () => [],
    },
    rowClassName: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showNewButton: {
      type: Boolean,
      default: false,
    },
    newButtonTitle: {
      type: String,
      default: '',
    },
    newButtonLoading: {
      type: Boolean,
      default: false,
    },
    filter: {
      type: Object,
      default: () => ({}),
    },
    order: {
      type: Object,
      default: () => ({}),
    },
    showDragOverTopTargets: {
      type: Array,
      default: () => [],
    },
    showDragOverBottomTargets: {
      type: Array,
      default: () => [],
    },
    stickyHeader: {
      type: Boolean,
      default: false,
    },
  },
  components: { MDragzone, MCard, MBtn, MDropdown, MIcon, MPagination, MSelectItem, MTextField },
  data() {
    return { search: {}, localOrder: {}, page: 1, focusedFilterOption: null };
  },
  emits: [
    'input:filter',
    'update:filter',
    'input:order',
    'update:order',
    'new',
    'contextmenu',
  ],
  computed: {
    newButtonLabel() {
      if (this.newButtonTitle === '') {
        return this.$t('general.new');
      }

      return this.newButtonTitle;
    },
    showLoadMoreButton() {
      return this.paginationType === paginationType.append
          && this.paginatedData.length < this.preparedDataSource.length;
    },
    showPagination() {
      return this.paginationType === paginationType.replace
          && this.pagination !== false
          && this.dataSource.length > this.pagination.pageSize;
    },
    preparedDataSource() {
      if ((Object.keys(this.search).length === 0 || Object.keys(this.search).filter((k) => this.search[k] !== '').length === 0)
          && (Object.keys(this.localOrder).length === 0 || Object.keys(this.localOrder).filter((k) => this.localOrder[k] !== '').length === 0)) {
        return this.dataSource;
      }

      const d = this.dataSource.filter((item) => {
        let ok = true;
        Object.keys(this.search).forEach((key) => {
          const column = this.getColumn(key);
          if (!column.onFilter(this.search[key], item)) {
            ok = false;
          }
        });

        return ok;
      });

      if (Object.keys(this.localOrder).length === 0) {
        return d;
      }

      Object.keys(this.localOrder).forEach((key) => {
        const column = this.getColumn(key);
        d.sort(column.sorter);
        if (this.localOrder[key] === 'down') {
          d.reverse();
        }
      });

      return d;
    },
    paginatedData() {
      if (this.pagination === false) {
        return this.preparedDataSource;
      }
      return this.paginate(this.preparedDataSource);
    },
  },
  methods: {
    paginate(data) {
      if (this.paginationType === paginationType.append) {
        return data.slice(0, this.page * this.pagination.pageSize);
      }
      return data.slice((this.page - 1) * this.pagination.pageSize, this.page * this.pagination.pageSize);
    },
    byString(o, s) {
      const str = s.replace(/\[(\w+)\]/g, '.$1').replace(/^\./, '');
      return str.split('.').reduce((obj, k) => (obj == null ? obj : obj[k]), o);
    },
    handleLoadMoreClick() {
      this.page += 1;
    },
    handleHeaderClick(column) {
      if (column.sorter === undefined) {
        return;
      }

      Object.keys(this.localOrder).filter((key) => key !== column.key).forEach((key) => {
        delete this.localOrder[key];
      });

      if (this.localOrder[column.key] === 'up') {
        this.localOrder[column.key] = 'down';
        return;
      }
      if (this.localOrder[column.key] === 'down') {
        this.localOrder[column.key] = '';
        return;
      }
      this.localOrder[column.key] = 'up';
    },
    iconColor(column) {
      if (this.search[column.key] !== '' && this.search[column.key] !== undefined) {
        return this.$colors.blue.base;
      }
      return this.$colors.grey.lighten1;
    },
    getColumn(key) {
      const f = this.columns.filter((c) => c.key === key);
      if (f.length === 0) {
        throw new Error(`column not found: ${key}`);
      }

      return f[0];
    },
    handleReset(column) {
      delete this.search[column.key];
    },
    setColumnFilter(column, value) {
      if (value === '') {
        delete this.search[column.key];
        return;
      }
      this.search[column.key] = value;
    },
    headerCellPadding(column) {
      let marginRight = 0;
      if (column.onFilter !== undefined) {
        marginRight += 2.4;
      }
      return `${marginRight}rem`;
    },
    handleRowClick(row) {
      if (row.disabled) {
        return;
      }
      this.customRow(row).on.click();
    },
    handleContextMenu(event, row) {
      if (row.disabled) {
        return;
      }
      this.$emit('contextmenu', event, row);
    },
  },
  watch: {
    localOrder: {
      handler() {
        if (JSON.stringify(this.localOrder) !== JSON.stringify(this.order)) {
          this.$emit('input:order', this.localOrder);
          this.$emit('update:order', this.localOrder);
        }
      },
      deep: true,
    },
    order: {
      handler() {
        if (JSON.stringify(this.localOrder) !== JSON.stringify(this.order)) {
          this.localOrder = copy(this.order);
        }
      },
      deep: true,
    },
    search: {
      handler() {
        if (JSON.stringify(this.search) !== JSON.stringify(this.filter)) {
          this.$emit('input:filter', this.search);
          this.$emit('update:filter', this.search);
        }
      },
      deep: true,
    },
    filter: {
      handler() {
        if (JSON.stringify(this.search) !== JSON.stringify(this.filter)) {
          this.search = copy(this.filter);
        }
      },
      deep: true,
    },
    dataSource() {
      this.page = 1;
    },
  },
  created() {
    this.localOrder = copy(this.order);
    this.search = copy(this.filter);
  },
};
</script>

<style
    scoped
    lang="scss"
    type="text/scss"
>
  @import "shared/assets/scss/padding";
  @import "shared/assets/scss/loading";

  .m-table-wrapper {
    .m-table {
      width: 100%;
      text-align: left;
      border-spacing: 0;
      border-collapse: separate;
      border-radius: 4px 4px 0 0;

      ._head {
        position: relative;
        height: 3.6rem;
        padding: .4rem 1.2rem;
        font-size: $font-size-3;
        font-weight: $font-weight-medium;
        color: $font-color-secondary;
        white-space: nowrap;
        border-top: 1px solid $border-color;
        border-bottom: 1px solid $border-color;
        border-radius: 0;

        &.-sticky {
          position: sticky;
          background-color: white;

          &.-sticky-header {
            top: 0;
            z-index: 1;
          }

          &.-sticky-col {
            left: 0;
            z-index: 2;
          }
        }

        ._header-cell {
          display: flex;
          align-items: center;

          ._title {
            display: flex;
            gap: .8rem;
            align-items: center;
            justify-content: space-between;
            width: 100%;

            ._title-left {
              display: flex;
              align-items: center;
              min-width: 0;
              padding: .2rem .6rem;
              margin: -.2rem -.6rem;
              border-radius: $border-radius-sm;

              &.-clickable {
                cursor: pointer;

                &:hover {
                  background-color: $hover-color;
                }
              }

              // Holds the sort arrow's space outside the hover background, so sorting doesn't widen the column.
              &.-sorter-space {
                margin-right: 1rem;
              }

              ._title-icon {
                margin-right: .6rem;
              }

              ._title-text {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;

                // Browsers round font metrics per OS, which puts 13px text up to 2px off-center (e.g. Chrome on Linux).
                // Trimming to the cap height centers the capitals for any font; the padding keeps descenders unclipped.
                @supports (text-box-trim: trim-both) {
                  padding: .5rem 0;
                  text-box-trim: trim-both;
                  text-box-edge: cap alphabetic;
                }
              }

              ._title-sorter {
                margin-left: .4rem;
              }
            }

            ._title-right {
              display: flex;
              align-items: center;
              margin-left: auto;
            }
          }

          ._actions {
            position: absolute;
            top: 0;
            right: .6rem;
            bottom: 0;
            display: flex;
            align-items: center;

            ._sort {
              position: relative;

              ._up {
                position: absolute;
                top: .5rem;
                left: .8rem;
              }

              ._down {
                position: absolute;
                top: 1.2rem;
                left: .8rem;
              }
            }
          }

          ._filter {
            margin-left: auto;
          }
        }
      }

      td {
        height: 4rem;
        padding: .6rem 1.2rem;
        overflow-wrap: break-word;
        border-bottom: 1px solid $border-color;
      }

      .m-table-tbody {
        tr {
          &.m-table-row {
            position: relative;

            &.-clickable {
              cursor: pointer;

              &:hover {
                &:not(.-selected) {
                  background-color: $hover-color;
                }

                &.-selected {
                  background-color: $highlighted-color-light;
                }
              }
            }

            &.-selected {
              background-color: $highlighted-color;
            }

            &.-disabled {
              ._cell {
                opacity: .4;
              }
            }
          }

          &:last-child td {
            border-bottom: none;
          }
        }

        ._dragzone-top {
          top: -2px;
        }

        ._dragzone-bottom {
          bottom: -2px;
        }

        &.-loading {
          @include loading;
        }
      }
    }

    ._empty {
      padding: 1rem 1.2rem;
      color: $font-color-tertiary;

      &:hover {
        background-color: white;
      }
    }

    ._bottom {
      position: sticky;
      left: 0;

      ._count {
        display: flex;
        gap: .6rem;
        align-items: baseline;
        margin: 1rem 1.2rem .2rem;
        font-size: $font-size-2;
        color: $font-color-secondary;

        ._label {
          color: $font-color-tertiary;
        }
      }

      ._pagination {
        margin-top: 1.2rem;
      }
    }

    &.-layout-padding {
      .m-table {
        @include layoutPaddingX;
      }

      ._bottom {
        @include layoutPaddingX;
      }
    }

    ._row-btn {
      cursor: pointer;

      &.-loading {
        pointer-events: none;
        cursor: default;
      }

      ._content {
        display: flex;
        align-items: center;
        color: $font-color-secondary;

        ._icon {
          margin-right: .8rem;
        }
      }

      &.-clickable {
        cursor: pointer;

        &:hover {
          background-color: $hover-color;
        }
      }
    }
  }

  ._filter-overlay {
    ._filter-actions {
      display: flex;
      align-items: center;
      justify-content: space-between;

      ._btn {
        flex: 1 1 auto;

        &:first-of-type {
          margin-right: .8rem;
        }
      }
    }

    ._filter-options {
      display: flex;
      flex-direction: column;
      min-width: 16rem;
    }
  }
</style>
